// ---------------------------------------------------------------------------
// Robust FAQ search
// ---------------------------------------------------------------------------
// A lightweight, dependency-free relevance engine for the support assistant.
// It replaces the old "exact / substring" match (which silently failed on any
// phrasing the user didn't copy verbatim) with token-based scoring that is
// tolerant of word order, plurals, typos in spacing, and domain jargon.
//
// Scoring blends:
//   • weighted token overlap (question tokens count more than answer tokens)
//   • whole-phrase / substring bonus
//   • domain synonym expansion (e.g. "moq" ⇄ "minimum order quantity")
// Results are returned ranked with a normalised 0–1 confidence so the UI can
// decide whether to answer directly, offer choices, or escalate.
// ---------------------------------------------------------------------------

export interface FAQItem {
  id?: number;
  question: string;
  answer: string;
  /**
   * Grouping used by the FAQ accordion (audit T7). One of the five buyer
   * journeys the curated set is organised around; missing/unknown values fall
   * into a trailing "More questions" group rather than being dropped.
   */
  category?: string;
}

/** Render order for the curated FAQ groups. */
export const FAQ_CATEGORIES = [
  'Ordering',
  'Compliance & Certification',
  'Shipping to the UK',
  'Shipping to the EU',
  'Custom & Private Label',
] as const;

export interface ScoredFAQ {
  item: FAQItem;
  score: number; // raw relevance score (higher = better)
  coverage: number; // fraction of query tokens this item accounts for (0–1)
  titleHits: number; // how many query tokens matched the FAQ *question* itself
  titleCoverage: number; // titleHits / query tokens (0–1)
}

/** Where the assistant should take the conversation for a given message. */
export type Tier = 'answer' | 'suggest' | 'none';

export interface Classification {
  tier: Tier;
  results: ScoredFAQ[];
}

/**
 * Conversational intents the FAQ index cannot answer on its own (audit H5, H7,
 * M2, M4). Detected from the raw message before scoring so small talk, human
 * hand-off and "where are you based" get a purposeful reply instead of a
 * "closest matches" list or the fallback.
 */
export type Intent =
  | 'greeting'
  | 'thanks'
  | 'human'
  | 'pricing'
  | 'location'
  | 'hours'
  | 'ship-elsewhere'
  | 'non-english';

// Very common words carry no signal  - drop them so they don't inflate scores.
const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'is',
  'are',
  'am',
  'be',
  'to',
  'of',
  'for',
  'and',
  'or',
  'in',
  'on',
  'at',
  'by',
  'with',
  'from',
  'as',
  'it',
  'its',
  'this',
  'that',
  'these',
  'those',
  'do',
  'does',
  'did',
  'can',
  'could',
  'would',
  'should',
  'will',
  'what',
  'whats',
  'which',
  'who',
  'whom',
  'how',
  'when',
  'where',
  'why',
  'your',
  'you',
  'we',
  'our',
  'us',
  'i',
  'me',
  'my',
  'have',
  'has',
  'had',
  'if',
  'about',
  'into',
  'out',
  'up',
  'down',
  'any',
  'some',
  'there',
  'their',
  'they',
  'them',
  'please',
  'tell',
  'give',
  'get',
  'got',
  'want',
  'need',
  'know',
  'many',
  'much',
]);

// Domain vocabulary → expansion terms. Lets a buyer's shorthand match the FAQ
// wording and vice-versa. All keys/values are lower-case, single or multi-word.
const SYNONYMS: Record<string, string[]> = {
  moq: ['minimum', 'order', 'quantity'],
  'minimum order quantity': ['moq'],
  price: ['pricing', 'cost', 'rate', 'quote', 'quotation'],
  pricing: ['price', 'cost', 'rate', 'quote'],
  cost: ['price', 'pricing', 'rate'],
  quote: ['quotation', 'pricing', 'price'],
  lead: ['leadtime', 'delivery', 'time'],
  leadtime: ['lead', 'time', 'delivery'],
  ship: ['shipping', 'freight', 'delivery', 'logistics'],
  shipping: ['freight', 'delivery', 'logistics', 'container', 'sea', 'air'],
  freight: ['shipping', 'container', 'logistics'],
  delivery: ['shipping', 'lead', 'time'],
  sample: ['samples', 'swatch', 'trial'],
  samples: ['sample', 'trial'],
  custom: ['customisation', 'customization', 'private', 'label', 'branding', 'logo', 'oem', 'odm'],
  // Both spellings resolve to the same concept, in either direction, so a UK
  // buyer typing "customisation" and a US buyer typing "customization" both
  // reach the same answers (audit V1.4 made the site copy British English).
  customisation: ['custom', 'customization', 'private', 'label', 'branding'],
  customization: ['custom', 'customisation', 'private', 'label', 'branding'],
  mould: ['mold', 'moulds', 'tooling', 'custom'],
  mold: ['mould', 'moulds', 'tooling', 'custom'],
  // Terms the curated UK/EU FAQ introduced (audit M4, M5).
  eori: ['customs', 'import', 'gb', 'uk', 'clearance'],
  hs: ['hscode', 'commodity', 'code', 'tariff', 'customs'],
  hscode: ['hs', 'commodity', 'code', 'tariff'],
  incoterm: ['incoterms', 'fob', 'cif', 'ddp', 'dap', 'exw', 'shipping'],
  incoterms: ['incoterm', 'fob', 'cif', 'ddp', 'dap', 'exw'],
  pfas: ['forever', 'chemicals', 'plasticfree', 'safety'],
  phytosanitary: ['phyto', 'plant', 'areca', 'certificate', 'customs'],
  ispm: ['pallet', 'pallets', 'fumigation', 'heat', 'treated'],
  pallet: ['pallets', 'ispm', 'packing', 'loadability'],
  loadability: ['container', 'carton', 'cartons', 'units', 'pallet'],
  epr: ['packaging', 'ppwr', 'recycling', 'compliance'],
  logo: ['branding', 'print', 'custom', 'private', 'label'],
  cert: ['certification', 'certificate', 'compliance'],
  certification: ['certificate', 'compliance', 'en13432', 'fda', 'lfgb'],
  compostable: ['biodegradable', 'compost', 'compostability', 'en13432'],
  biodegradable: ['compostable', 'eco', 'compost'],
  eco: ['ecofriendly', 'sustainable', 'biodegradable', 'compostable', 'green'],
  bagasse: ['sugarcane', 'pulp'],
  areca: ['palm', 'leaf'],
  plate: ['plates', 'tableware', 'disposable'],
  payment: ['pay', 'terms', 'tt', 'lc', 'advance'],
  duty: ['duties', 'tariff', 'tariffs', 'customs'],
  tariff: ['duty', 'duties', 'customs'],
  vat: ['tax', 'duty', 'import', 'customs'],
  eu: ['europe', 'european'],
  uk: ['britain', 'british'],
  return: ['returns', 'refund', 'replacement', 'policy'],
  contact: ['email', 'phone', 'call', 'whatsapp', 'sales', 'reach'],
  capacity: ['production', 'volume', 'units'],
};

const normalise = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ') // strip punctuation / symbols
    .replace(/\s+/g, ' ')
    .trim();

// Light stemming: collapse trivial plurals so "plates"≈"plate", "duties"≈"duty".
// Kept deliberately simple and *consistent*  - both the query and the FAQ index
// run through it, so the only requirement is that variants map to one root.
const stem = (w: string): string => {
  if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y';
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
};

const tokenize = (text: string): string[] =>
  normalise(text)
    .split(' ')
    .filter((w) => w.length > 1 && !STOP_WORDS.has(w))
    .map(stem);

// All search variants of a token: itself plus any domain synonyms (stemmed).
const variantsOf = (token: string): string[] => {
  const syns = SYNONYMS[token];
  return syns ? [token, ...syns.map(stem)] : [token];
};

// Expand a token set with domain synonyms (also stemmed) to widen recall.
const expand = (tokens: string[]): Set<string> => {
  const out = new Set(tokens);
  for (const t of tokens) for (const v of variantsOf(t)) out.add(v);
  return out;
};

// Per-item token index, memoised so repeated searches are cheap. The question
// is synonym-expanded too, so a buyer's wording matches the FAQ either way.
const indexCache = new WeakMap<FAQItem, { q: Set<string>; a: Set<string> }>();

const indexOf = (item: FAQItem) => {
  let idx = indexCache.get(item);
  if (!idx) {
    idx = { q: expand(tokenize(item.question)), a: new Set(tokenize(item.answer)) };
    indexCache.set(item, idx);
  }
  return idx;
};

// Bounded Levenshtein: true when `a` and `b` are within `max` edits. Early-exits
// as soon as a row's best cost exceeds the budget, so it stays cheap on the tiny
// FAQ index. Used for typo tolerance (audit H2).
const withinEditDistance = (a: string, b: string, max: number): boolean => {
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > max) return false;
  let prev = Array.from({ length: lb + 1 }, (_, j) => j);
  for (let i = 1; i <= la; i++) {
    const curr = [i];
    let rowMin = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      curr[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return false;
    prev = curr;
  }
  return prev[lb] <= max;
};

// Typo tolerance sized to word length, and requiring a shared first letter so
// short words don't collide ("cost"≈"cast"). Words under 4 chars must match
// exactly. "minimun"≈"minimum", "quantty"≈"quantity", "cutlry"≈"cutlery".
const fuzzyMatch = (token: string, candidate: string): boolean => {
  if (token === candidate) return true;
  const len = Math.min(token.length, candidate.length);
  if (len < 4) return false;
  if (!token.startsWith(candidate[0])) return false;
  return withinEditDistance(token, candidate, len >= 7 ? 2 : 1);
};

const fuzzyInSet = (token: string, set: Set<string>): boolean => {
  if (set.has(token)) return true;
  if (token.length < 4) return false;
  for (const cand of set) if (fuzzyMatch(token, cand)) return true;
  return false;
};

// How well a single query word matches one FAQ: 3 = in the question (strongest
// signal of intent), 1 = in the answer body, 0 = miss. Synonyms widen an exact
// look-up; fuzzy matching (original word only, to bound false positives) then
// forgives typos.
const matchLevel = (raw: string, idx: { q: Set<string>; a: Set<string> }): number => {
  for (const v of variantsOf(raw)) if (idx.q.has(v)) return 3;
  if (fuzzyInSet(raw, idx.q)) return 3;
  for (const v of variantsOf(raw)) if (idx.a.has(v)) return 1;
  if (fuzzyInSet(raw, idx.a)) return 1;
  return 0;
};

/**
 * Rank FAQs against a free-text query.
 * @returns matches sorted by descending relevance, capped at `limit`.
 */
export function searchFAQs(query: string, faqs: FAQItem[], limit = 4): ScoredFAQ[] {
  const rawTokens = tokenize(query);
  if (rawTokens.length === 0) return [];
  const normQuery = normalise(query);

  const scored: ScoredFAQ[] = [];

  for (const item of faqs) {
    const idx = indexOf(item);
    let titleHits = 0;
    let bodyHits = 0;

    // Score per *raw* intent word  - synonyms and fuzzing only widen where we
    // look, they never inflate the denominator, so coverage reflects real user
    // intent.
    for (const raw of rawTokens) {
      const lvl = matchLevel(raw, idx);
      if (lvl === 3) titleHits++;
      else if (lvl === 1) bodyHits++;
    }

    const satisfied = titleHits + bodyHits;
    if (satisfied === 0) continue;

    const coverage = satisfied / rawTokens.length;
    const titleCoverage = titleHits / rawTokens.length;
    // Whole-phrase bonus: the question literally contains the user's words.
    const phraseBonus =
      normQuery.length >= 3 && normalise(item.question).includes(normQuery) ? 4 : 0;

    // A question-title hit is worth far more than an answer-body mention, so an
    // FAQ squarely about the topic outranks ones that merely allude to a word.
    // This is what makes "MOQ", "are you PFAS free" and "how long does delivery
    // take" answer directly instead of returning a list (audit H1).
    const score = titleHits * 5 + bodyHits + coverage * 1.5 + titleCoverage * 2 + phraseBonus;
    scored.push({ item, score, coverage, titleHits, titleCoverage });
  }

  scored.sort((x, y) => y.score - x.score);
  return scored.slice(0, limit);
}

/**
 * How much the top hit dominates the runner-up before we answer directly rather
 * than offer a shortlist. Deliberately gentle: a distinctive answer-body word
 * (e.g. "Rotterdam") should tip a near-tie, while a genuine tie ("ports" → UK
 * vs EU) stays a shortlist.
 */
const DOMINANCE = 1.25;

/**
 * Route a message to one of three behaviours (audit H1, H5): answer the top FAQ
 * directly, offer a "closest matches" shortlist, or escalate to a human.
 *
 *  - `answer`  - a question-title match that clearly out-scores the rest.
 *  - `suggest` - real but ambiguous overlap (several plausible FAQs).
 *  - `none`    - nothing on-topic; the floor below rejects stray answer-body
 *                hits from off-topic input ("weather in London" grazing
 *                "London Gateway") so it escalates instead of guessing.
 */
export function classifyQuery(query: string, faqs: FAQItem[]): Classification {
  const results = searchFAQs(query, faqs);
  if (results.length === 0) return { tier: 'none', results };

  const [top, second] = results;

  // Floor: nothing hit a question title and the best match barely overlaps the
  // query. Almost always off-topic - don't answer, don't tease a list.
  if (top.titleHits === 0 && top.coverage < 0.5) return { tier: 'none', results: [] };

  const dominant = !second || top.score >= second.score * DOMINANCE;
  if (top.titleHits >= 1 && dominant) return { tier: 'answer', results };

  return { tier: 'suggest', results };
}

/**
 * Back-compat helper. Prefer {@link classifyQuery}, which also distinguishes
 * "offer a shortlist" from "escalate".
 */
export function isConfidentMatch(results: ScoredFAQ[]): boolean {
  if (results.length === 0) return false;
  const [top, second] = results;
  const dominant = !second || top.score >= second.score * DOMINANCE;
  return top.titleHits >= 1 && dominant;
}

// Words that mark a message as written in a major EU language rather than
// English (audit M2). Kept to unambiguous function words so an English message
// never trips it. Only consulted once the FAQ search has already found nothing.
const NON_ENGLISH_MARKERS = new Set([
  // German
  'ist',
  'die',
  'der',
  'das',
  'und',
  'wie',
  'was',
  'mindestbestellmenge',
  'ich',
  'sie',
  'wir',
  'haben',
  'können',
  'konnen',
  'für',
  'fur',
  'nicht',
  'bitte',
  // French
  'quelle',
  'quel',
  'est',
  'les',
  'des',
  'une',
  'vous',
  'nous',
  'pour',
  'commande',
  'livraison',
  'bonjour',
  'combien',
  'minimale',
  // Spanish
  'cuál',
  'cual',
  'qué',
  'que',
  'los',
  'las',
  'una',
  'para',
  'pedido',
  'mínimo',
  'minimo',
  'hola',
  'cuánto',
  'cuanto',
  'envío',
  'envio',
  // Italian
  'quale',
  'sono',
  'della',
  'delle',
  'per',
  'ordine',
  'minimo',
  'spedizione',
  'ciao',
  // Dutch
  'wat',
  'het',
  'een',
  'hoe',
  'kunnen',
  'bestelling',
  'verzending',
  'minimale',
]);

/**
 * Detect a conversational intent the FAQ index cannot serve on its own. Runs on
 * the raw message. `faqMatched` lets callers suppress the softer intents
 * (pricing/location/hours/ship-elsewhere) when the search already has a
 * confident FAQ answer, while hard intents (greeting/thanks/human) always win.
 */
export function detectIntent(query: string, faqMatched = false): Intent | null {
  const q = normalise(query);
  if (!q) return null;
  const has = (re: RegExp) => re.test(q);

  // ── Hard intents: always take priority over FAQ matching ──────────────────
  if (
    has(
      /^(hi|hello|hey|heya|hiya|yo|hai|hallo|good (morning|afternoon|evening|day)|greetings|namaste|howdy)\b/
    ) &&
    q.split(' ').length <= 4
  ) {
    return 'greeting';
  }
  if (has(/\b(thanks|thank you|thankyou|thx|cheers|appreciate it|much appreciated)\b/)) {
    return 'thanks';
  }
  if (
    has(/\b(human|real person|live agent|customer service|customer support|account manager)\b/) ||
    has(
      /\b(speak|talk|chat|connect|get)\b[^.]*\b(human|person|someone|somebody|agent|representative|rep|advisor|adviser|sales|team|expert|staff|to you)\b/
    ) ||
    has(
      /\b(call me|contact me|call me back|get in touch|reach (a|your|the) (person|team|sales|human))\b/
    )
  ) {
    return 'human';
  }

  // ── Soft intents: only when the FAQ search wasn't confident ───────────────
  if (!faqMatched) {
    if (
      has(
        /\b(price|prices|priced|pricing|cost|costs|how much|per (unit|piece|plate|item)|rate card|discount|cheapest|expensive)\b/
      )
    ) {
      return 'pricing';
    }

    const shipVerb = has(
      /\b(ship|shipping|deliver|delivery|export|send|freight|supply|dispatch)\b/
    );
    // Split across three patterns so no single regex trips Sonar's complexity
    // limit; a match in any means the message names a destination we don't list.
    const elsewhere =
      has(
        /\b(usa|u s a|united states|america|american|canada|canadian|australia|australian|new zealand)\b/
      ) ||
      has(
        /\b(uae|dubai|qatar|saudi|singapore|malaysia|japan|china|hong kong|south africa|nigeria|kenya)\b/
      ) ||
      has(
        /\b(worldwide|globally|international(ly)?|other countries|outside (the )?(uk|eu|europe))\b/
      );
    if (shipVerb && elsewhere) return 'ship-elsewhere';

    if (
      has(
        /\b(where are you|where.?s your|where is your|based|located|location|head ?office|headquarter|headquarters|hq|which country|what country|your address)\b/
      )
    ) {
      return 'location';
    }
    if (
      has(
        /\b(opening hours|office hours|business hours|working hours|hours of operation|time ?zone|when are you open|when do you open|what time)\b/
      )
    ) {
      return 'hours';
    }
  }

  return null;
}

/**
 * A last-resort guess that a no-match message is written in a non-English EU
 * language, so the assistant can offer help in that language rather than a
 * bare fallback (audit M2). Only trustworthy after the FAQ search found nothing.
 */
export function looksNonEnglish(query: string): boolean {
  const tokens = normalise(query).split(' ').filter(Boolean);
  if (tokens.length < 2) return false;
  return tokens.filter((t) => NON_ENGLISH_MARKERS.has(t)).length >= 2;
}

/** True when the leading word signals the message continues the last topic. */
export function isFollowUp(query: string): boolean {
  return /^(and|also|plus|ok|okay)\b|^(what|how) about\b|^(and|but) (what|how) about\b/i.test(
    query.trim()
  );
}
